import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { httpClient } from '@/src/@core/lib/httpClient'

export interface RefundedBy {
  id: number
  firstName: string
  lastName: string
  email: string
}

export interface RefundDetail {
  refundAmount: number
  refundStatus: string
  reason: string
  refundDate: string
  refundTransactionId: string
  refundedBy: RefundedBy
}

export interface PaymentRefundReport {
  restaurant: string
  orderNumber: number
  paymentId: string
  paymentSource: string
  amount: number
  paymentStatus: string
  paymentDate: string
  paymentTransactionId: string
  paymentTransactionStatus: string
  refunds: RefundDetail[]
  totalRefundedAmount: number
  isFullyRefunded: boolean
}

export interface PaymentRefundReportState {
  refundReports: PaymentRefundReport[]
  gygPaymentsByOrderId: { [orderId: string]: any[] }
  loading: boolean
  error: string | null
}

const initialState: PaymentRefundReportState = {
  refundReports: [],
  gygPaymentsByOrderId: {},
  loading: false,
  error: null
}

export const getPaymentByOrderId = createAsyncThunk('payment/getPaymentByOrderId', async (orderId: string) => {
  const response = await httpClient.get(`/payments?orderId=${orderId}`)

  return response.data
})

export const getPaymentRefundReport = createAsyncThunk(
  'payment/getRefundReport',
  async (params: { dateRangeType: 'MONTH' | 'CUSTOM'; monthYear?: string; startDate?: string; endDate?: string }) => {
    const response = await httpClient.get('/payments/refund-report', { params })

    // The API returns { data: PaymentRefundReport[] }
    return response.data.data
  }
)

export const paymentSlice = createSlice({
  name: 'payment',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getPaymentRefundReport.pending, (state: PaymentRefundReportState) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getPaymentRefundReport.fulfilled,
        (state: PaymentRefundReportState, action: PayloadAction<PaymentRefundReport[]>) => {
          state.loading = false
          state.refundReports = action.payload
        }
      )
      .addCase(getPaymentRefundReport.rejected, (state: PaymentRefundReportState, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch refund report'
      })

      // getPaymentByOrderId
      .addCase(getPaymentByOrderId.pending, (state: PaymentRefundReportState) => {
        state.loading = true
        state.error = null
      })
      .addCase(
        getPaymentByOrderId.fulfilled,
        (state: PaymentRefundReportState, action: PayloadAction<any, string, { arg: string }>) => {
          state.loading = false
          if (action.meta && action.meta.arg) {
            const orderId = action.meta.arg
            state.gygPaymentsByOrderId[orderId] = action.payload?.data || []
          }
        }
      )
      .addCase(getPaymentByOrderId.rejected, (state: PaymentRefundReportState, action) => {
        state.loading = false
        state.error = action.error.message || 'Failed to fetch payment by order ID'
      })
  }
})

export default paymentSlice.reducer
